import { Link } from 'react-router-dom';
import { Lock, ShieldCheck, KeyRound, Landmark, FileText, EyeOff } from 'lucide-react';
import MarketingShell from '../components/homepage/MarketingShell';

const PRACTICES = [
  {
    icon: Lock, color: '#27B84A', title: 'Encryption everywhere',
    desc: 'All traffic is encrypted in transit with TLS, and your documents are stored on encrypted cloud storage.',
  },
  {
    icon: KeyRound, color: '#3B82F6', title: 'Secure authentication',
    desc: 'Hashed passwords, session controls, and multi-factor authentication on administrative accounts.',
  },
  {
    icon: Landmark, color: '#14B8A6', title: 'Bank connections via Plaid',
    desc: 'Bank logins go directly to Plaid — the same connection layer used by major financial apps. CountBucks never sees or stores your bank credentials.',
  },
  {
    icon: ShieldCheck, color: '#8B5CF6', title: 'Payments via Stripe',
    desc: 'Card and payment processing is handled by Stripe, a PCI-DSS Level 1 certified provider. Card numbers never touch our servers.',
  },
  {
    icon: EyeOff, color: '#FF7816', title: 'Role-based access',
    desc: 'Workspace roles, client-portal scoping and per-membership permissions keep every user limited to exactly what they should see.',
  },
  {
    icon: FileText, color: '#EAB308', title: 'Full audit trail',
    desc: 'Every meaningful change in your books is written to an audit log, so you always know who did what and when.',
  },
];

export default function SecurityHub() {
  return (
    <MarketingShell title="Security | CountBucks">
      <section className="mx-auto max-w-7xl px-4 pb-20 pt-14 sm:px-6" data-testid="security-hub-page">
        <span className="inline-block rounded-full border border-[#27B84A]/40 bg-[#F2FBF4] px-4 py-1.5 text-xs font-bold tracking-widest text-[#118A35] dark:bg-[#0E1A11] dark:text-[#27B84A]">
          SECURITY & TRUST
        </span>
        <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
          Your books deserve <span className="text-[#27B84A]">bank-grade care.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-base text-[#596273] sm:text-lg dark:text-[#AEB6B1]">
          CountBucks is built by a CPA-owned firm that handles sensitive financial data every day. Here's how we protect yours.
        </p>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PRACTICES.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`security-card-${p.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="rounded-3xl border border-[#E4E8E6] bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/5 dark:border-[#1C2420] dark:bg-[#10150F]"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${p.color}1A` }}>
                  <Icon className="h-6 w-6" style={{ color: p.color }} />
                </span>
                <h2 className="mt-4 text-lg font-bold">{p.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-[#596273] dark:text-[#8B948F]">{p.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 rounded-3xl border border-[#E4E8E6] bg-[#F7F9F8] p-7 dark:border-[#1C2420] dark:bg-[#0D120E]">
          <h2 className="text-lg font-bold">Found a security issue?</h2>
          <p className="mt-2 max-w-2xl text-sm text-[#596273] dark:text-[#8B948F]">
            We take reports seriously and respond quickly. Reach out through our{' '}
            <Link to="/contact" className="font-bold text-[#118A35] hover:underline dark:text-[#27B84A]">contact page</Link>{' '}
            and mention "security" in the subject line for priority handling.
          </p>
        </div>
      </section>
    </MarketingShell>
  );
}
